import LexicalParser from 'app/services/formula/parser/lexicalParser';
import SyntaxParser, { syntaxParser } from 'app/services/formula/parser/syntaxParser';
import { getFormattedRootFields } from 'app/services/formula/parser/syntaxParser/parserUtils';
import { getProactiveSuggestions, prettifyName } from 'app/services/formula/suggestionEngine';
import _ from 'lodash';

import { ARRAY_FUNCTION_ENUM, FIELD_KEYS } from 'constants/parser';

export const ANCHOR_MARGIN = 6;

export const REFRESH_DEBOUNCE_MS = 70;

// An accepted insertion "opens a new context" to chain into when it ends with an
// entity dot ('Path.'), a function paren ('FN('), or an operator's trailing space —
// the popover should re-trigger. A completed leaf field or value ends in a word
// character (or closing quote); re-triggering there only re-opens the popover on the
// item the user just picked, so the caller suppresses it instead.
export const insertOpensNewContext = inserted => /[.\s(]$/.test(inserted ?? '');

const getTrailingWordLength = text => (text.match(/([\w.]+)$/)?.[1] ?? '').length;

const gatherFallbackFields = ({
  editorValue,
  schemaParams,
  isOnlyBooleanAllowed,
  isNewExpressionsParsingLogicEnabled,
}) => {
  if (!schemaParams?.entities) {
    return [];
  }

  if (!editorValue) {
    return getFormattedRootFields(schemaParams);
  }

  const lexicalParser = new LexicalParser(schemaParams);
  const tokens = lexicalParser.parse(editorValue);

  if (isNewExpressionsParsingLogicEnabled) {
    const parser = new SyntaxParser(schemaParams);
    const { fields = [] } = parser.parse({ tokens, isOnlyBooleanAllowed });

    return fields;
  }

  const { fields = [] } = syntaxParser({ tokens, schemaParams, isOnlyBooleanAllowed });

  return fields;
};

const buildFallbackSuggestions = ({
  textBeforeCursor,
  editorValue,
  schemaParams,
  isOnlyBooleanAllowed,
  isNewExpressionsParsingLogicEnabled,
}) => {
  const trailingWord = textBeforeCursor.match(/([\w]*)$/)?.[1] ?? '';

  const fields = gatherFallbackFields({
    editorValue,
    schemaParams,
    isOnlyBooleanAllowed,
    isNewExpressionsParsingLogicEnabled,
  });

  const functions =
    trailingWord || !editorValue
      ? Object.keys(schemaParams?.functions ?? {}).map(name => ({
          key: 'Functions',
          name,
          value: name,
        }))
      : [];

  const matcher = new RegExp(_.escapeRegExp(trailingWord), 'gim');

  const suggestions = [...fields, ...functions]
    .filter(item => item.value !== trailingWord && item.value.match(matcher))
    .map(item => ({
      key: item.key,
      value: item.value,
      name: item.name,
      insertText: item.insertText ?? item.value,
    }));

  return { replaceLength: getTrailingWordLength(textBeforeCursor), suggestions };
};

/**
 * Resolve suggestions from the proactive engine, falling back to parser-driven
 * field/function gathering when the engine has no opinion.
 *
 * @param {{ textBeforeCursor: string, editorValue: string, schemaParams: Object, isOnlyBooleanAllowed: boolean, isNewExpressionsParsingLogicEnabled: boolean }} params
 * @returns {{ replaceLength: number, suggestions: Array }}
 */
export const resolveSuggestions = ({
  textBeforeCursor,
  editorValue,
  schemaParams,
  isOnlyBooleanAllowed,
  isNewExpressionsParsingLogicEnabled,
}) => {
  const proactive = getProactiveSuggestions({
    textBeforeCursor,
    schemaParams,
    isOnlyBooleanAllowed,
  });

  if (!_.isNil(proactive)) {
    return proactive;
  }

  return buildFallbackSuggestions({
    textBeforeCursor,
    editorValue,
    schemaParams,
    isOnlyBooleanAllowed,
    isNewExpressionsParsingLogicEnabled,
  });
};

const matchRank = (a, b) => {
  const aPrefix = a.matchIndex === 0 ? 0 : 1;
  const bPrefix = b.matchIndex === 0 ? 0 : 1;

  if (aPrefix !== bPrefix) {
    return aPrefix - bPrefix;
  }

  return a.matchIndex - b.matchIndex;
};

/**
 * Keep only suggestions whose value contains the typed text (case-insensitive)
 * and rank prefix matches first, then by match position. Empty `typed` returns
 * the suggestions unchanged so contexts like operators/values still show all.
 *
 * @param {Array} suggestions
 * @param {string} typed
 * @returns {Array}
 */
export const filterRankSuggestions = (suggestions, typed) => {
  if (!typed) {
    return suggestions;
  }

  const needle = typed.toLowerCase();

  return suggestions
    .map(suggestion => ({
      suggestion,
      matchIndex: String(suggestion.value ?? '')
        .toLowerCase()
        .indexOf(needle),
    }))
    .filter(entry => entry.matchIndex >= 0)
    .sort(matchRank)
    .map(entry => entry.suggestion);
};

const SUGGESTION_GROUP = {
  FIELDS: 'Fields',
  FUNCTIONS: 'Functions',
  OPERATORS: 'Operators',
  ITERATORS: 'Iterators',
  ENUM_TYPES: 'EnumTypes',
  VALUES: 'Values',
  TIMEZONES: 'Timezones',
};

const GROUP_TITLE = {
  [SUGGESTION_GROUP.FIELDS]: 'Fields',
  [SUGGESTION_GROUP.FUNCTIONS]: 'Functions',
  [SUGGESTION_GROUP.OPERATORS]: 'Operators',
  [SUGGESTION_GROUP.ITERATORS]: 'Iterators',
  [SUGGESTION_GROUP.ENUM_TYPES]: 'Values',
  [SUGGESTION_GROUP.VALUES]: 'Values',
  [SUGGESTION_GROUP.TIMEZONES]: 'Values',
};

const GROUP_ORDER = [
  SUGGESTION_GROUP.FIELDS,
  SUGGESTION_GROUP.ITERATORS,
  SUGGESTION_GROUP.FUNCTIONS,
  SUGGESTION_GROUP.OPERATORS,
  SUGGESTION_GROUP.ENUM_TYPES,
  SUGGESTION_GROUP.VALUES,
  SUGGESTION_GROUP.TIMEZONES,
];

export const TYPE_CHIP = {
  FN: 'T-FN',
  ENUM: 'T-ENUM',
  STRING: 'T-STRING',
  NUMBER: 'T-NUMBER',
  DATE: 'T-DATE',
  BOOL: 'T-BOOL',
  ENTITY: 'T-ENTITY',
  ARRAY: 'T-ARRAY',
  ITERATOR: 'T-ITERATOR',
  NEUTRAL: 'T-NEUTRAL',
};

const CHIP_LABEL = {
  'T-FN': 'fn',
  'T-ENUM': 'enum',
  'T-STRING': 'string',
  'T-NUMBER': 'number',
  'T-DATE': 'date',
  'T-BOOL': 'bool',
  'T-ENTITY': 'object',
  'T-ARRAY': 'list',
  'T-ITERATOR': 'item',
  'T-NEUTRAL': 'op',
};

// A drillable field suggestion (object/entity the user can dot into) ends with a
// trailing dot or has no scalar type marker — surface it with the entity chip
// instead of falling through to the neutral 'op' chip.
const isEntitySuggestion = suggestion => {
  const value = suggestion.value ?? '';

  return value.endsWith('.') || suggestion.isEntity === true;
};

// FIELD_KEYS.EnumTypes / FIELD_KEYS.Timezones ship as spaced human strings
// ('Enum Types', 'Timezones'); normalize both raw and SCREAMING forms.
const KEY_TO_GROUP = {
  Fields: SUGGESTION_GROUP.FIELDS,
  Functions: SUGGESTION_GROUP.FUNCTIONS,
  Operators: SUGGESTION_GROUP.OPERATORS,
  Iterators: SUGGESTION_GROUP.ITERATORS,
  Values: SUGGESTION_GROUP.VALUES,
  EnumTypes: SUGGESTION_GROUP.ENUM_TYPES,
  [FIELD_KEYS.EnumTypes]: SUGGESTION_GROUP.ENUM_TYPES,
  [FIELD_KEYS.Timezones]: SUGGESTION_GROUP.TIMEZONES,
  Timezones: SUGGESTION_GROUP.TIMEZONES,
};

const normalizeGroupKey = key => KEY_TO_GROUP[key] ?? SUGGESTION_GROUP.FIELDS;

const NUMERIC_SUFFIXES = ['decimal', 'double', 'integer', 'number', 'int', 'long', 'percent'];
const STRING_SUFFIXES = ['string', 'text', 'guid'];

const detectFieldChip = name => {
  const lower = (name ?? '').toLowerCase();

  if (lower.includes('array')) {
    return TYPE_CHIP.ARRAY;
  }

  if (lower.includes('enum')) {
    return TYPE_CHIP.ENUM;
  }

  if (lower.includes('bool')) {
    return TYPE_CHIP.BOOL;
  }

  if (lower.includes('date')) {
    return TYPE_CHIP.DATE;
  }

  if (NUMERIC_SUFFIXES.some(suffix => lower.includes(suffix))) {
    return TYPE_CHIP.NUMBER;
  }

  if (STRING_SUFFIXES.some(suffix => lower.includes(suffix))) {
    return TYPE_CHIP.STRING;
  }

  return TYPE_CHIP.NEUTRAL;
};

/**
 * Map a suggestion to its type chip identifier.
 *
 * @param {{ key: string, name?: string }} suggestion
 * @returns {string}
 */
export const getTypeChip = suggestion => {
  const group = normalizeGroupKey(suggestion.key);

  if (group === SUGGESTION_GROUP.FUNCTIONS) {
    return TYPE_CHIP.FN;
  }

  if (group === SUGGESTION_GROUP.ENUM_TYPES) {
    return TYPE_CHIP.ENUM;
  }

  if (group === SUGGESTION_GROUP.VALUES || group === SUGGESTION_GROUP.TIMEZONES) {
    return TYPE_CHIP.STRING;
  }

  if (group === SUGGESTION_GROUP.ITERATORS) {
    return TYPE_CHIP.ITERATOR;
  }

  if (group === SUGGESTION_GROUP.OPERATORS) {
    return TYPE_CHIP.NEUTRAL;
  }

  if (isEntitySuggestion(suggestion)) {
    return TYPE_CHIP.ENTITY;
  }

  return detectFieldChip(suggestion.name);
};

const getChipLabel = chip => CHIP_LABEL[chip] ?? CHIP_LABEL['T-NEUTRAL'];

/**
 * Split a name into highlighted/plain parts based on a case-insensitive
 * match of the typed partial. Returns a single plain part when there is
 * no match or no typed text.
 *
 * @param {string} name
 * @param {string} typed
 * @returns {Array<{ text: string, match: boolean }>}
 */
export const buildNameParts = (name, typed) => {
  const safeName = name ?? '';

  if (!typed) {
    return [{ text: safeName, match: false }];
  }

  const lowerName = safeName.toLowerCase();
  const index = lowerName.indexOf(typed.toLowerCase());

  if (index < 0) {
    return [{ text: safeName, match: false }];
  }

  const parts = [];
  const before = safeName.slice(0, index);
  const matched = safeName.slice(index, index + typed.length);
  const after = safeName.slice(index + typed.length);

  if (before) {
    parts.push({ text: before, match: false });
  }

  parts.push({ text: matched, match: true });

  if (after) {
    parts.push({ text: after, match: false });
  }

  return parts;
};

const isFieldGroup = group => group === SUGGESTION_GROUP.FIELDS;

const buildDisplay = (suggestion, group) => {
  const displayName = suggestion.value ?? '';

  if (group === SUGGESTION_GROUP.FUNCTIONS) {
    // Function param signatures stay deferred (need suggestion-engine enrichment).
    return { name: suggestion.name ?? displayName, signature: null, description: null };
  }

  if (group === SUGGESTION_GROUP.ENUM_TYPES) {
    // Enum value rows preview their sibling value set (cheaply available on the
    // suggestion); show it as the secondary `.sig` line.
    return {
      name: displayName,
      signature: suggestion.siblingPreview ?? null,
      description: suggestion.name && suggestion.name !== displayName ? suggestion.name : null,
    };
  }

  if (isFieldGroup(group) || group === SUGGESTION_GROUP.ITERATORS) {
    return { name: displayName, signature: null, description: suggestion.name ?? null };
  }

  return { name: displayName, signature: null, description: suggestion.name ?? null };
};

/**
 * Build the ordered, grouped option list consumed by AutocompletePopover.
 * Each option carries a flat `index` matching its position in `suggestions`
 * so keyboard/mouse selection can address it directly.
 *
 * @param {{ suggestions: Array, typed: string }} params
 * @returns {Array<{ title: string, options: Array }>}
 */
const TITLE_ORDER = GROUP_ORDER.reduce((acc, group) => {
  const title = GROUP_TITLE[group];

  return acc.includes(title) ? acc : [...acc, title];
}, []);

const FIELDS_TITLE = GROUP_TITLE[SUGGESTION_GROUP.FIELDS];
const FUNCTIONS_TITLE = GROUP_TITLE[SUGGESTION_GROUP.FUNCTIONS];

const resolveGroupTitle = (title, scope) => {
  if (title === FIELDS_TITLE && scope?.collection) {
    return `Fields on ${scope.collection}`;
  }

  return title;
};

const resolveGroupHint = (title, scope) => {
  if (title === FUNCTIONS_TITLE && scope?.collection) {
    return 'Did you mean a function?';
  }

  return null;
};

export const buildGroups = ({ suggestions, typed, scope }) => {
  const buckets = new Map();

  suggestions.forEach((suggestion, index) => {
    const group = normalizeGroupKey(suggestion.key);
    const title = GROUP_TITLE[group];
    const chip = getTypeChip(suggestion);
    const display = buildDisplay(suggestion, group);

    const option = {
      index,
      chip,
      chipLabel: getChipLabel(chip),
      nameParts: buildNameParts(display.name, typed),
      signature: display.signature,
      description: display.description,
    };

    if (!buckets.has(title)) {
      buckets.set(title, []);
    }

    buckets.get(title).push(option);
  });

  return TITLE_ORDER.filter(title => buckets.has(title)).map(title => ({
    title: resolveGroupTitle(title, scope),
    hint: resolveGroupHint(title, scope),
    options: buckets.get(title),
  }));
};

const COLLECTION_FUNCTIONS = new Set(Object.values(ARRAY_FUNCTION_ENUM));

const VALID_PATH_RE = /^[\w.]+$/;

const deriveScopeIterator = arrayPath => {
  if (!arrayPath || !VALID_PATH_RE.test(arrayPath)) {
    return '_x';
  }

  const lastSegment = arrayPath.toLowerCase().split('.').pop();
  const stripped = lastSegment.endsWith('s') ? lastSegment.slice(0, -1) : lastSegment;

  return `_${stripped}`;
};

/**
 * Best-effort scope detection: scan textBeforeCursor for the nearest still-open
 * collection function call and read its first argument (the iterated array path).
 * Returns { collection, iterator } or null when no open collection lambda is found.
 *
 * @param {string} textBeforeCursor
 * @returns {{ collection: string, iterator: string }|null}
 */
export const detectScope = textBeforeCursor => {
  const text = textBeforeCursor ?? '';
  const openCalls = [];

  let depth = 0;
  let inString = false;
  let identStart = -1;

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];

    if (char === "'") {
      inString = !inString;
      identStart = -1;
      continue;
    }

    if (inString) {
      continue;
    }

    if (/[A-Za-z0-9_.]/.test(char)) {
      identStart = identStart < 0 ? i : identStart;
      continue;
    }

    if (char === '(') {
      const ident = identStart >= 0 ? text.slice(identStart, i) : '';
      openCalls.push({ name: ident, depth, argStart: i + 1 });
      depth += 1;
    }

    if (char === ')') {
      depth -= 1;
      const lastOpen = openCalls.length ? openCalls[openCalls.length - 1] : null;

      if (lastOpen && lastOpen.depth === depth) {
        openCalls.pop();
      }
    }

    identStart = -1;
  }

  const enclosing = [...openCalls].reverse().find(call => COLLECTION_FUNCTIONS.has(call.name));

  if (!enclosing) {
    return null;
  }

  const argParts = text.slice(enclosing.argStart).split(',');
  const arg = argParts[0].trim();
  const declaredIterator = (argParts[1] ?? '').trim();
  const collectionPath = VALID_PATH_RE.test(arg) ? arg : '';
  const lastSegment = collectionPath ? collectionPath.split('.').pop() : enclosing.name;
  const iterator = /^_[A-Za-z]\w*$/.test(declaredIterator)
    ? declaredIterator
    : deriveScopeIterator(collectionPath);

  return {
    collection: prettifyName(lastSegment) || lastSegment,
    iterator,
  };
};
